<template>
  <div class="pdf-viewer">
    <template v-if="src">
      <!-- Toolbar — sadece single modda -->
      <div v-if="mode === 'single'" class="pdf-viewer__toolbar">
        <div class="pdf-viewer__group">
          <button class="pdf-viewer__btn" :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">
            <i class="uil-angle-left"></i>
          </button>
          <span class="pdf-viewer__page-info">
            {{ currentPage }} / {{ totalPages }}
          </span>
          <button class="pdf-viewer__btn" :disabled="currentPage >= totalPages" @click="goToPage(currentPage + 1)">
            <i class="uil-angle-right"></i>
          </button>
        </div>
        <div class="pdf-viewer__separator"></div>
        <div class="pdf-viewer__group">
          <button class="pdf-viewer__btn" @click="zoomOut" :disabled="zoomLevel <= 0.5">
            <i class="uil-minus"></i>
          </button>
          <span class="pdf-viewer__zoom-info">{{ Math.round(zoomLevel * 100) }}%</span>
          <button class="pdf-viewer__btn" @click="zoomIn" :disabled="zoomLevel >= 1.5">
            <i class="uil-plus"></i>
          </button>
        </div>
        <div class="pdf-viewer__separator"></div>
        <div class="pdf-viewer__group">
          <button class="pdf-viewer__btn" @click="rotate">
            <i class="uil-rotate-360"></i>
          </button>
          <button class="pdf-viewer__btn" @click="resetZoom">
            <i class="uil-history"></i>
          </button>
        </div>
      </div>

      <!-- PDF Content -->
      <div ref="pdfContainer" class="pdf-viewer__container" @scroll="onContainerScroll">
        <div v-if="loading" class="pdf-viewer__placeholder">
          <div class="pdf-viewer__skeleton" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </div>
        <div v-if="error" class="pdf-viewer__placeholder">
          <span class="text-danger">{{ $t('general.pdf_load_error') || 'PDF yüklenemedi' }}</span>
        </div>

        <!-- Single mod — tek canvas -->
        <canvas v-if="mode === 'single'" ref="pdfCanvas" class="pdf-viewer__page" v-show="!loading && !error"/>

        <!-- Scroll mod — tüm sayfalar alt alta -->
        <div v-if="mode === 'scroll' && !loading && !error" class="pdf-viewer__scroll-pages">
          <div v-for="page in totalPages" :key="page" class="pdf-viewer__page-wrapper">
            <div v-if="!renderedPages[page]" class="pdf-viewer__page-skeleton" aria-hidden="true"></div>
            <canvas
              :ref="'pageCanvas_' + page"
              :class="{ 'pdf-viewer__page--hidden': !renderedPages[page] }"
              class="pdf-viewer__page pdf-viewer__page--scroll"
            />
          </div>
        </div>
      </div>
    </template>
    <div v-else class="pdf-viewer__placeholder">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
  </div>
</template>

<script>
/*
  reached-end emit kuralları:
  ┌────────────┬──────────────────────┬──────────────────────┐
  │ Sayfa      │ single               │ scroll               │
  ├────────────┼──────────────────────┼──────────────────────┤
  │ 1 sayfa    │ hemen true           │ hemen true           │
  │ 2-4 sayfa  │ son sayfaya gitmeli  │ hemen true           │
  │ 5+ sayfa   │ son sayfaya gitmeli  │ scroll ile alta inmeli│
  └────────────┴──────────────────────┴──────────────────────┘
*/
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export default {
  name: "PdfViewer",
  props: {
    src: {
      type: String,
      default: ''
    },
    mode: {
      type: String,
      default: 'single',
      validator: (v) => ['single', 'scroll'].includes(v)
    }
  },
  data() {
    return {
      loading: false,
      error: false,
      totalPages: 0,
      currentPage: 1,
      zoomLevel: 1,
      rotation: 0,
      pdfDoc: null,
      loadingTask: null,
      loadToken: 0,
      renderedPages: {},
      rendering: false,
      baseScale: 1,
      reachedEnd: false
    };
  },
  watch: {
    src(val) {
      if (val) {
        this.currentPage = 1;
        this.zoomLevel = 1;
        this.rotation = 0;
        this.reachedEnd = false;
        this.loadPdf(val);
      }
    }
  },
  mounted() {
    if (this.src) {
      this.loadPdf(this.src);
    }
  },
  beforeDestroy() {
    this.loadToken += 1;
    if (this.loadingTask) {
      this.loadingTask.destroy();
      this.loadingTask = null;
    }
    if (this.pdfDoc) {
      this.pdfDoc.destroy();
      this.pdfDoc = null;
    }
  },
  methods: {
    async loadPdf(url) {
      const loadToken = ++this.loadToken;
      this.loading = true;
      this.error = false;
      this.totalPages = 0;
      this.renderedPages = {};

      if (this.loadingTask) {
        this.loadingTask.destroy();
        this.loadingTask = null;
      }

      if (this.pdfDoc) {
        this.pdfDoc.destroy();
        this.pdfDoc = null;
      }

      try {
        // Çok sayfalı sertifikalarda sunucunun Range isteklerini desteklememesi,
        // PDF.js'in ikinci ve sonraki sayfalarda yüklemeyi kesmesine yol açabiliyor.
        // Dosyayı tek istekte indirerek bu bağımlılığı kaldırıyoruz; akış
        // indirmesini açık tutarak ek bir tam-dosya beklemesi oluşturmuyoruz.
        this.loadingTask = pdfjsLib.getDocument({
          url,
          disableRange: true
        });
        const pdf = await this.loadingTask.promise;

        if (loadToken !== this.loadToken) {
          pdf.destroy();
          return;
        }

        this.pdfDoc = pdf;
        this.loadingTask = null;
        this.totalPages = pdf.numPages;
        this.currentPage = 1;

        this.loading = false;
        await this.$nextTick();

        const shouldAutoComplete =
          this.totalPages <= 1 ||
          (this.mode === 'scroll' && this.totalPages < 5);

        if (shouldAutoComplete) {
          this.reachedEnd = true;
          this.$emit('reached-end');
        }

        if (this.mode === 'scroll') {
          await this.renderAllPages(loadToken);
        } else {
          await this.renderPage(this.currentPage, loadToken);
        }
      } catch (e) {
        if (loadToken !== this.loadToken) return;

        console.error('PDF load error:', e);
        this.error = true;
        this.loading = false;
        this.$emit('load-error', e);
      }
    },

    async renderAllPages(loadToken = this.loadToken) {
      if (!this.pdfDoc) return;

      const containerWidth = this.$refs.pdfContainer
        ? this.$refs.pdfContainer.clientWidth - 32
        : 800;

      for (let i = 1; i <= this.totalPages; i++) {
        if (loadToken !== this.loadToken) return;

        const page = await this.pdfDoc.getPage(i);
        const dpr = window.devicePixelRatio || 1;
        const renderScale = Math.max(dpr, 2);

        const viewport = page.getViewport({ scale: 1 });
        const scale = containerWidth / viewport.width;

        const renderViewport = page.getViewport({ scale: scale * renderScale });
        const displayViewport = page.getViewport({ scale: scale });

        const canvasRef = this.$refs['pageCanvas_' + i];
        const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef;
        if (!canvas) continue;

        canvas.width = renderViewport.width;
        canvas.height = renderViewport.height;
        canvas.style.width = displayViewport.width + 'px';
        canvas.style.height = displayViewport.height + 'px';

        await page.render({
          canvasContext: canvas.getContext('2d'),
          viewport: renderViewport
        }).promise;

        if (loadToken !== this.loadToken) return;

        this.$set(this.renderedPages, i, true);

        // İlk sayfayı hemen görünür kıl; sonraki sayfalar kullanıcı ilk
        // içeriği beklemeden arka arkaya işlenmeye devam eder.
        if (i === 1) {
          await this.$nextTick();
          await new Promise(resolve => window.requestAnimationFrame(resolve));
        }
      }
    },

    async renderPage(pageNum, loadToken = this.loadToken) {
      if (!this.pdfDoc || this.rendering) return;
      this.rendering = true;

      try {
        const page = await this.pdfDoc.getPage(pageNum);
        if (loadToken !== this.loadToken) return;
        const dpr = window.devicePixelRatio || 1;
        const renderScale = Math.max(dpr, 2);

        const viewport = page.getViewport({ scale: 1, rotation: this.rotation });

        if (this.zoomLevel === 1) {
          const containerWidth = this.$refs.pdfContainer
            ? this.$refs.pdfContainer.clientWidth
            : 800;
          this.baseScale = containerWidth / viewport.width;
        }

        const finalScale = this.baseScale * this.zoomLevel;
        const renderViewport = page.getViewport({ scale: finalScale * renderScale, rotation: this.rotation });
        const displayViewport = page.getViewport({ scale: finalScale, rotation: this.rotation });

        const canvas = this.$refs.pdfCanvas;
        if (!canvas) return;

        canvas.width = renderViewport.width;
        canvas.height = renderViewport.height;
        canvas.style.width = displayViewport.width + 'px';
        canvas.style.height = displayViewport.height + 'px';

        await page.render({
          canvasContext: canvas.getContext('2d'),
          viewport: renderViewport
        }).promise;
      } finally {
        this.rendering = false;
      }
    },

    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.renderPage(page);
      if (page === this.totalPages) {
        this.$emit('reached-end');
      }
    },

    zoomIn() {
      if (this.zoomLevel >= 1.5) return;
      this.zoomLevel = Math.round((this.zoomLevel + 0.25) * 100) / 100;
      this.renderPage(this.currentPage);
    },

    zoomOut() {
      if (this.zoomLevel <= 0.5) return;
      this.zoomLevel = Math.round((this.zoomLevel - 0.25) * 100) / 100;
      this.renderPage(this.currentPage);
    },

    resetZoom() {
      this.zoomLevel = 1;
      this.rotation = 0;
      this.renderPage(this.currentPage);
    },

    rotate() {
      this.rotation = (this.rotation + 90) % 360;
      this.renderPage(this.currentPage);
    },

    onContainerScroll() {
      if (this.mode !== 'scroll' || this.reachedEnd) return;
      const el = this.$refs.pdfContainer;
      if (!el) return;
      // Alt kenara 20px kala emit et
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
        this.reachedEnd = true;
        this.$emit('reached-end');
      }
    }
  }
}
</script>

<style scoped>
.pdf-viewer {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.pdf-viewer__toolbar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  border-radius: 10px 10px 0 0;
  flex-shrink: 0;
}

.pdf-viewer__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  border: none;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 10px;
  color: #333;
  font-size: 20px;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-appearance: none;
  transition: transform 0.1s ease, background 0.1s ease;
}

.pdf-viewer__btn:focus {
  outline: none;
}

.pdf-viewer__btn:active:not(:disabled) {
  transform: scale(0.92);
  background: rgba(0, 0, 0, 0.12);
}

.pdf-viewer__btn:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.pdf-viewer__page-info,
.pdf-viewer__zoom-info {
  font-size: 14px;
  font-weight: 600;
  color: #444;
  min-width: 48px;
  text-align: center;
  user-select: none;
}

.pdf-viewer__group {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pdf-viewer__separator {
  width: 1px;
  height: 24px;
  background: #d0d0d0;
  margin: 0 4px;
}

.pdf-viewer__container {
  width: 100%;
  max-height: 70vh;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  background: #e8e8e8;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 16px;
}

.pdf-viewer__page {
  display: block;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: #fff;
  flex-shrink: 0;
}

/* Scroll mod — sayfalar alt alta */
.pdf-viewer__scroll-pages {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.pdf-viewer__page--scroll {
  max-width: 100%;
}

.pdf-viewer__page-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
  min-height: 280px;
}

.pdf-viewer__page--hidden {
  display: none;
}

.pdf-viewer__skeleton,
.pdf-viewer__page-skeleton {
  width: min(100%, 640px);
  min-height: 280px;
  border-radius: 4px;
  background: linear-gradient(90deg, #d9d9d9 25%, #eeeeee 50%, #d9d9d9 75%);
  background-size: 200% 100%;
  animation: pdf-viewer-skeleton 1.25s ease-in-out infinite;
}

.pdf-viewer__skeleton {
  min-height: 60vh;
}

@keyframes pdf-viewer-skeleton {
  to {
    background-position: -200% 0;
  }
}

.pdf-viewer__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

@media (max-width: 768px) {
  .pdf-viewer__toolbar {
    flex-wrap: nowrap;
    justify-content: space-between;
    gap: 4px;
    padding: 6px 8px;
  }

  .pdf-viewer__group {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 8px;
    padding: 2px;
    gap: 1px;
  }

  .pdf-viewer__btn {
    min-width: 32px;
    min-height: 32px;
    font-size: 16px;
    border-radius: 6px;
  }

  .pdf-viewer__page-info,
  .pdf-viewer__zoom-info {
    font-size: 12px;
    min-width: 32px;
  }

  .pdf-viewer__separator {
    display: none;
  }

  .pdf-viewer__container {
    max-height: 60vh;
    padding: 8px;
  }

  .pdf-viewer__scroll-pages {
    gap: 12px;
  }
}
</style>
